import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Star as FeatureIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import apiService from '../services/api';
import { Epic, User, CreateEpicData, Priority, IssueStatus } from '../types';

const Features: React.FC = () => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');

  const [features, setFeatures] = useState<Epic[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create Feature Modal State
  const [openCreateFeatureModal, setOpenCreateFeatureModal] = useState(false);
  const [createFeatureLoading, setCreateFeatureLoading] = useState(false);
  const [createFeatureForm, setCreateFeatureForm] = useState<CreateEpicData>({
    name: '',
    description: '',
    status: 'backlog',
    priority: Priority.MEDIUM,
    projectId: projectId || '',
    ownerId: '',
    goals: [],
    successMetrics: [],
    color: '#1976d2'
  });

  useEffect(() => {
    if (projectId) {
      loadFeatures();
      loadUsers();
      setCreateFeatureForm(prev => ({ ...prev, projectId }));
    }
  }, [projectId]);

  const loadFeatures = async () => {
    if (!projectId) return;

    try {
      setLoading(true);
      setError(null);
      const featuresData = await apiService.getEpics(projectId);
      setFeatures(featuresData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load features');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    if (!projectId) return;

    try {
      const usersData = await apiService.getProjectAssignments(projectId);
      setUsers(usersData.map((assignment: any) => assignment.user).filter(Boolean));
    } catch (err: any) {
      console.error('Failed to load users:', err);
    }
  };

  const handleCreateFeature = () => {
    setOpenCreateFeatureModal(true);
  };

  const handleCloseCreateFeatureModal = () => {
    setOpenCreateFeatureModal(false);
    setCreateFeatureForm({
      name: '',
      description: '',
      status: 'backlog',
      priority: Priority.MEDIUM,
      projectId: projectId || '',
      ownerId: '',
      goals: [],
      successMetrics: [],
      color: '#1976d2'
    });
  };

  const handleCreateFeatureSubmit = async () => {
    if (!createFeatureForm.name || !createFeatureForm.projectId) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setCreateFeatureLoading(true);
      setError(null);
      
      const newFeature = await apiService.createEpic(createFeatureForm);
      
      // Refresh features list
      await loadFeatures();
      
      handleCloseCreateFeatureModal();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create feature');
    } finally {
      setCreateFeatureLoading(false);
    }
  };

  const handleCreateFeatureFormChange = (field: keyof CreateEpicData, value: any) => {
    setCreateFeatureForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'backlog': return 'default';
      case 'todo': return 'info';
      case 'in_progress': return 'warning';
      case 'done': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            Features
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateFeature}
          >
            Create Feature
          </Button>
        </Box>

        {/* Features List */}
        {features.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <FeatureIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No features found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create your first feature to organize your stories and defects.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateFeature}
            >
              Create Feature
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {features.map((feature) => (
              <Grid item xs={12} md={6} lg={4} key={feature.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" component="div" sx={{ flex: 1 }}>
                        {feature.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                          label={feature.priority}
                          color={getPriorityColor(feature.priority) as any}
                          size="small"
                        />
                        <Chip
                          label={feature.status}
                          color={getStatusColor(feature.status) as any}
                          size="small"
                        />
                      </Box>
                    </Box>
                    
                    {feature.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {feature.description}
                      </Typography>
                    )}

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Stories:</strong> {feature.issues?.length || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Story Points:</strong> {feature.totalStoryPoints || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Progress:</strong> {feature.completionPercentage || 0}%
                      </Typography>
                    </Box>

                    {feature.owner && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                          <PersonIcon />
                        </Avatar>
                        <Typography variant="body2">
                          {feature.owner.firstName} {feature.owner.lastName}
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AssignmentIcon />}
                        onClick={() => {/* TODO: Navigate to feature detail */}}
                      >
                        View Stories
                      </Button>
                      <IconButton size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Create Feature Modal */}
      <Dialog open={openCreateFeatureModal} onClose={handleCloseCreateFeatureModal} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Feature</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Feature Name"
              fullWidth
              margin="normal"
              value={createFeatureForm.name}
              onChange={(e) => handleCreateFeatureFormChange('name', e.target.value)}
              required
            />
            <TextField
              label="Description"
              fullWidth
              margin="normal"
              multiline
              rows={4}
              value={createFeatureForm.description}
              onChange={(e) => handleCreateFeatureFormChange('description', e.target.value)}
            />
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Priority</InputLabel>
              <Select
                value={createFeatureForm.priority}
                onChange={(e) => handleCreateFeatureFormChange('priority', e.target.value as Priority)}
                label="Priority"
              >
                <MenuItem value={Priority.LOW}>Low</MenuItem>
                <MenuItem value={Priority.MEDIUM}>Medium</MenuItem>
                <MenuItem value={Priority.HIGH}>High</MenuItem>
                <MenuItem value={Priority.CRITICAL}>Critical</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Owner</InputLabel>
              <Select
                value={createFeatureForm.ownerId}
                onChange={(e) => handleCreateFeatureFormChange('ownerId', e.target.value)}
                label="Owner"
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.firstName} {user.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Target Date"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
              value={createFeatureForm.targetDate || ''}
              onChange={(e) => handleCreateFeatureFormChange('targetDate', e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateFeatureModal}>Cancel</Button>
          <Button 
            onClick={handleCreateFeatureSubmit} 
            variant="contained"
            disabled={createFeatureLoading || !createFeatureForm.name}
          >
            {createFeatureLoading ? <CircularProgress size={20} /> : 'Create Feature'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Features; 